"use strict";

/**
 * Super Admin -> Bounced Emails (WEB-3700).
 *
 * Manages the outbound email suppression list. Every address on the list is
 * stripped out of the TO list by the API's MailService before the mail reaches
 * SES, so a listed address receives NO Showsuite email until it is removed.
 * Rows arrive either automatically from the AWS SES permanent-bounce webhook
 * (source = Auto) or by hand from this page (source = Manual).
 *
 * Backed by the WEB-4001 endpoints:
 *   GET  sec/super/bounced-emails         SS_VW_BOUNCED_EMAILS
 *   POST sec/super/bounced-emails         SS_ADD_BOUNCED_EMAILS
 *   GET  sec/super/bounced-emails/delete  SS_DEL_BOUNCED_EMAILS (CORS-safe alias of DELETE)
 *
 * @remarks Addresses are stored lowercased server-side. This controller
 *          lowercases before POSTing so what the admin sees in the list is what
 *          they typed, and so a mixed-case entry cannot look like a no-op.
 * @remarks Removing an address does NOT repair the mailbox. If it is still
 *          undeliverable the next send hard-bounces and the SES webhook re-adds
 *          it automatically — the confirmation copy says so explicitly.
 */
class SuperBouncedEmailsController {

    constructor($scope, $timeout, SSHttpService, SSUtilService, SSAlertService,
                SSUserService, SSConfirmationService) {
        const self = this;

        self.$scope = $scope;
        self.$timeout = $timeout;
        self.SSHttpService = SSHttpService;
        self.SSUtilService = SSUtilService;
        self.SSAlertService = SSAlertService;
        self.SSUserService = SSUserService;
        self.SSConfirmationService = SSConfirmationService;

        self.data = SHOWSUITE.data;

        self.BOUNCED_EMAIL_SOURCE_AUTO = SHOWSUITE.BOUNCED_EMAIL_SOURCE_AUTO;
        self.BOUNCED_EMAIL_SOURCE_MANUAL = SHOWSUITE.BOUNCED_EMAIL_SOURCE_MANUAL;

        if (self.SSUserService.isSuper()) {
            self.init();
        }
    }

    init() {
        const self = this;

        self.apiOffset = 0;
        self.pageSizes = [20, 30, 50];
        self.apiLimit = self.pageSizes[0];
        self.selectedPage = 1;
        self.pages = 0;
        self.count = 0;

        // null (not []) so the table can distinguish "still loading" from
        // "loaded, genuinely empty".
        self.bouncedEmails = null;

        self.searchParams = self._defaultSearchParams();
        self.entry = {};
        self.errorMessage = '';

        self.initDatepickers();
        self.getBouncedEmails();
    }

    /**
     * Wire the Blocked On From/To inputs to jQuery UI datepicker.
     *
     * `onSelect` pushes the picked value back into ng-model through
     * `$scope.$apply` because jQuery UI fires its callback outside Angular's
     * digest. Same widget and dd/mm/yy format as the SAGE Audit Log filters.
     */
    initDatepickers() {
        const self = this;

        self.$timeout(function () {
            jQuery('#bouncedEmailsFromDate').datepicker({
                changeMonth: true,
                changeYear: true,
                dateFormat: 'dd/mm/yy',
                onSelect: function (dateText) {
                    self.$scope.$apply(function () {
                        self.searchParams.from_date = dateText;
                    });
                }
            });
            jQuery('#bouncedEmailsToDate').datepicker({
                changeMonth: true,
                changeYear: true,
                dateFormat: 'dd/mm/yy',
                onSelect: function (dateText) {
                    self.$scope.$apply(function () {
                        self.searchParams.to_date = dateText;
                    });
                }
            });
        });
    }

    /**
     * Fetch the current page of suppressed addresses.
     *
     * @remarks `send_total_count` is required for the pagination control to
     *          know how many pages exist — the endpoint returns `count: null`
     *          without it.
     */
    getBouncedEmails() {
        const self = this;

        let url = 'sec/super/bounced-emails';
        let params = {
            offset: self.apiOffset,
            limit: self.apiLimit,
            send_total_count: true
        };

        // Only send filters that are actually set — the endpoint validates
        // `source` against its enum and `from_date`/`to_date` as dates, so
        // blank strings would 412.
        if (self.searchParams.search) {
            params.search = self.searchParams.search;
        }
        if (self.searchParams.source) {
            params.source = self.searchParams.source;
        }
        if (self.searchParams.from_date) {
            params.from_date = self.formatDateForApi(self.searchParams.from_date);
        }
        if (self.searchParams.to_date) {
            params.to_date = self.formatDateForApi(self.searchParams.to_date);
        }

        self.bouncedEmails = null;

        loading(true);
        self.SSHttpService.getAPIRequest(url, params).then(function (response) {
            loading(false);
            if (response instanceof Error) {
                // Leave the table in its empty state rather than "Loading..."
                // forever when the request fails.
                self.bouncedEmails = [];
                self.SSAlertService.parseAndDisplayError(response);
                return;
            }

            // SSResponse omits `data` entirely when the payload is falsy, so
            // never dereference it blind.
            let data = (response && response.data) || {};

            self.bouncedEmails = data.bounced_email_addresses || [];
            self.count = data.count ? data.count : 0;

            self.pages = Math.ceil(self.count / self.apiLimit);
            self.selectedPage = Math.floor(self.apiOffset / self.apiLimit) + 1;
        });
    }

    /**
     * Apply the filters, restarting from page 1.
     */
    searchBouncedEmails() {
        const self = this;

        self.apiOffset = 0;
        self.selectedPage = 1;
        self.getBouncedEmails();
    }

    /**
     * Clear every filter and reload the unfiltered list.
     */
    resetSearch() {
        const self = this;

        self.searchParams = self._defaultSearchParams();
        self.searchBouncedEmails();
    }

    /**
     * Change page size, restarting from page 1 so the offset stays valid.
     *
     * @remarks parseInt is defensive — the select uses ng-options over a numeric
     *          array so apiLimit is already a number, and it must stay one or the
     *          offset arithmetic below turns into string concatenation.
     */
    setLimitAndReload() {
        const self = this;

        self.apiLimit = parseInt(self.apiLimit);
        self.searchBouncedEmails();
    }

    pagesGoBack() {
        const self = this;

        if (self.selectedPage <= 1) {
            return;
        }
        self.selectedPage--;
        self.apiOffset -= self.apiLimit;
        self.getBouncedEmails();
    }

    pagesGoNext() {
        const self = this;

        if (self.selectedPage >= self.pages) {
            return;
        }
        self.selectedPage++;
        self.apiOffset += self.apiLimit;
        self.getBouncedEmails();
    }

    /**
     * Open the Add Bounced Email modal on a clean entry.
     *
     * @remarks A `.modal.fade` will not open from ng-show alone — it has to be
     *          shown imperatively through jQuery.
     */
    openAddModal() {
        const self = this;

        self.entry = {};
        self.errorMessage = '';

        jQuery('#bouncedEmailAddModal').modal('show');
    }

    /**
     * Manually suppress an address.
     *
     * @remarks The already-suppressed case comes back as 412 sub_code 412002.
     *          Its human-readable text lives at `response.message`, NOT
     *          `response.data.message`, and `parseAndDisplayError` drops it in
     *          favour of a generic toast — so it is surfaced inline instead.
     */
    postBouncedEmail() {
        const self = this;

        self.errorMessage = '';

        if (!self.validateEntry()) {
            return;
        }

        let url = 'sec/super/bounced-emails';
        let params = {
            email: self.normalizeEmail(self.entry.email),
            reason: self.entry.reason ? self.entry.reason.trim() : null
        };

        loading(true);
        self.SSHttpService.postAPIRequest(url, params).then(function (response) {
            loading(false);
            if (response instanceof Error) {
                self.errorMessage = response.message
                    ? response.message
                    : 'Could not add this email address. Please try again.';
                return;
            }

            jQuery('#bouncedEmailAddModal').modal('hide');
            self.entry = {};
            self.SSAlertService.success('Email address added to the bounced emails list.');

            // Refetch rather than unshifting locally: the new row belongs at
            // whatever position the created_at DESC ordering puts it, and the
            // total count and page count both change.
            self.searchBouncedEmails();
        });
    }

    /**
     * Remove an address from the suppression list so it can receive email again.
     *
     * @remarks Uses the GET `/delete` alias rather than the DELETE verb — the
     *          established convention in this codebase for CORS reasons.
     * @param {Object} bouncedEmail The row to remove. Keyed by email; the table
     *        has no id column.
     */
    removeBouncedEmail(bouncedEmail) {
        const self = this;

        if (!bouncedEmail || !bouncedEmail.email) {
            return;
        }

        self.SSConfirmationService.getConfirmed(
            'Remove from Bounced Emails?',
            bouncedEmail.email + ' will start receiving Showsuite emails again immediately. ' +
            'If the mailbox is still invalid it will bounce again and be re-blocked automatically.',
            self.SSConfirmationService.TYPE_DEFAULT,
            function () {
                let url = 'sec/super/bounced-emails/delete';
                let params = {email: bouncedEmail.email};

                loading(true);
                self.SSHttpService.getAPIRequest(url, params).then(function (response) {
                    loading(false);
                    if (response instanceof Error) {
                        self.SSAlertService.parseAndDisplayError(response);
                        return;
                    }

                    jQuery.each(self.bouncedEmails, function (i, entry) {
                        if (entry.email == bouncedEmail.email) {
                            self.bouncedEmails.splice(i, 1);
                            return false;
                        }
                    });

                    if (self.count > 0) {
                        self.count--;
                        self.pages = Math.ceil(self.count / self.apiLimit);
                    }

                    self.SSAlertService.success('Email address removed from the bounced emails list.');
                });
            }
        );
    }

    /**
     * Validate the Add form before it is sent.
     *
     * @returns {boolean} True when the entry can be submitted.
     */
    validateEntry() {
        const self = this;

        if (!self.entry.email || !self.entry.email.trim()) {
            self.errorMessage = 'Email address is required.';
            return false;
        }

        // Mirrors the server's `email` rule so an obvious typo is caught before
        // the round trip.
        let emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailPattern.test(self.entry.email.trim())) {
            self.errorMessage = 'Please enter a valid email address.';
            return false;
        }

        if (self.entry.reason && self.entry.reason.length > 1000) {
            self.errorMessage = 'Reason cannot be longer than 1000 characters.';
            return false;
        }

        return true;
    }

    /**
     * Human-readable label for `source`.
     *
     * @param {number} source One of BOUNCED_EMAIL_SOURCE_AUTO / _MANUAL.
     * @returns {string}
     */
    getSourceLabel(source) {
        const self = this;

        if (source == self.BOUNCED_EMAIL_SOURCE_MANUAL) {
            return 'Manual';
        }

        // Pre-WEB-4001 rows were all created by the SES webhook and the column
        // defaults to Auto, so anything else reads as Auto.
        return 'Auto (Bounced)';
    }

    /**
     * Date + time for display, or a dash when absent.
     *
     * @remarks The API omits NULL date fields entirely, and
     *          `formatUtcToLocalTime` is not guarded against undefined —
     *          `moment.utc(undefined)` is "now", which would render a phantom
     *          timestamp. Hence the explicit falsy check first.
     * @param {string|null|undefined} dateTime
     * @returns {string}
     */
    getFormattedDateTime(dateTime) {
        const self = this;

        if (!dateTime) {
            return '--';
        }

        return self.SSUtilService.formatUtcToLocalDate(dateTime) + ' ' +
            self.SSUtilService.formatUtcToLocalTime(dateTime);
    }

    /**
     * Canonical storage form of an address — matches the server's normalization.
     *
     * @param {string} email
     * @returns {string}
     */
    normalizeEmail(email) {
        return email ? email.trim().toLowerCase() : email;
    }

    /**
     * Convert a dd/mm/yyyy datepicker value to the yyyy-mm-dd the API expects.
     *
     * Handles the string the jQuery UI datepicker writes into ng-model as well
     * as a Date object, in case the input is ever bound to a different widget.
     *
     * @param {string|Date|null} date
     * @returns {string|null}
     */
    formatDateForApi(date) {
        if (!date) {
            return null;
        }

        if (typeof date === 'string') {
            let parts = date.split('/');
            if (parts.length === 3 && parts[2].length === 4) {
                return parts[2] + '-' + parts[1] + '-' + parts[0];
            }
            return date;
        }

        let pad = function (n) {
            return n < 10 ? '0' + n : '' + n;
        };

        return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate());
    }

    _defaultSearchParams() {
        return {
            search: '',
            source: '',
            from_date: '',
            to_date: ''
        };
    }
}

SuperBouncedEmailsController.$inject = [
    '$scope',
    '$timeout',
    'SSHttpService',
    'SSUtilService',
    'SSAlertService',
    'SSUserService',
    'SSConfirmationService'
];
app.controller('SuperBouncedEmailsController', SuperBouncedEmailsController);
